<template>
  <div class="slider-captcha" :class="'is-' + status">
    <!-- 题面：背景图 + 缺块，尺寸按服务端给出的像素坐标系等比缩放 -->
    <div class="sc-stage" ref="stage" :style="{ height: stageHeight + 'px' }">
      <img v-if="bgSrc" class="sc-bg" :src="bgSrc" alt="" draggable="false">
      <img
        v-if="puzzleSrc && status !== 'success'"
        class="sc-puzzle"
        :src="puzzleSrc"
        :style="puzzleStyle"
        alt=""
        draggable="false">
      <div
        v-if="status === 'loading' || status === 'loadFailed'"
        class="sc-veil"
        @click="status === 'loadFailed' && load()">
        <a-icon :type="status === 'loading' ? 'loading' : 'reload'" :spin="status === 'loading'"/>
        <span class="sc-veil__text">
          {{ status === 'loading' ? $t('user.slider.loading') : $t('user.slider.loadFailed') }}
        </span>
      </div>
      <button
        v-if="status !== 'loading' && status !== 'loadFailed' && status !== 'success'"
        type="button"
        class="sc-refresh"
        @click="load">
        <a-icon type="reload"/>
      </button>
    </div>

    <!-- 滑轨：整条凹槽，把手左移即出题 -->
    <div class="sc-track">
      <div class="sc-fill" :style="{ width: (offset + handleSize) + 'px' }"></div>
      <span class="sc-tip" :style="tipStyle">{{ tipText }}</span>
      <button
        ref="handle"
        type="button"
        class="sc-handle"
        :style="{ left: offset + 'px' }"
        @pointerdown.prevent="onDragStart"
        @mousedown.prevent="onDragStart"
        @touchstart.prevent="onDragStart"
        @pointerup="onDragEnd"
        @mouseup="onDragEnd"
        @touchend="onDragEnd"
        @keydown.left.prevent="nudge(-8)"
        @keydown.right.prevent="nudge(8)"
        @keydown.enter.prevent="verify()"
        @keydown.space.prevent="verify()">
        <a-icon :type="handleIcon"/>
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mark, markFail, clearTrace, flush } from '@/utils/sliderDiag'

// 服务端 node-puzzle 的坐标系（与 keiyo-server 的 SLIDER_* 常量一致）
const BG_WIDTH = 320
const BG_HEIGHT = 180
const HANDLE_SIZE = 44
// 验证失败后停留片刻再换题，让使用者看见失败状态
const RETRY_DELAY = 800
// 请求无任何回调时的兜底时长
const WATCHDOG = 12000
// 按下后一直没有 move：手势被浏览器判成滚动/缩放，或命中的根本不是把手（拖不动）
const NO_MOVE_WAIT = 700
// 拖动中再无 move/up 空档超过这个时长：松手事件没送到组件（拖到位没反应）
const NO_RELEASE_WAIT = 2000

export default {
  name: 'SliderCaptcha',
  data () {
    return {
      status: 'loading', // loading | default | moving | verify | success | error | loadFailed
      captchaId: '',
      bgSrc: '',
      puzzleSrc: '',
      stageWidth: 0,
      puzzleWidth: 60,
      puzzleHeight: 60,
      puzzleTop: 0,
      offset: 0,
      dragging: false,
      // 本次按下的输入类型：pointer / mouse / touch。
      // 一次按下只绑定一套监听器，避免 pointerdown 之后浏览器再补发的
      // mousedown / touchstart 把 dragStartX 重置，拖动起点会跳一下
      dragMode: '',
      activePointerId: null,
      // 触摸拖动锁定的那根手指：多指时 touches[0] 可能是早就压在屏幕上的另一根
      activeTouchId: null,
      dragStartX: 0,
      handleSize: HANDLE_SIZE,
      errorCount: 0,
      // 换题后丢弃上一次请求的回调，避免旧响应污染新题
      reqSeq: 0,
      // 题面请求是否在途：初始 status 也是 loading，不能拿状态当“已发请求”的判据
      requesting: false,
      watchdogTimer: null,
      retryTimer: null,
      // 弹窗刚打开时容器可能还量不到宽度，用有限次数的补量兜底
      measureFrame: null,
      measureTries: 0,
      // 诊断现场：本次手势的采样（次数/首末坐标/时刻），只用于失败上报
      diag: null,
      noMoveTimer: null,
      releaseTimer: null,
      lastProbeAt: 0
    }
  },
  computed: {
    stageHeight () {
      if (!this.stageWidth) {
        return 0
      }
      return Math.round(this.stageWidth * BG_HEIGHT / BG_WIDTH)
    },
    pieceLeft () {
      const pieceMax = this.stageWidth - this.puzzleDisplayWidth
      const moveMax = this.stageWidth - this.handleSize
      if (moveMax <= 0) {
        return 0
      }
      return this.offset * pieceMax / moveMax
    },
    puzzleDisplayWidth () {
      return this.stageWidth ? Math.round(this.stageWidth * this.puzzleWidth / BG_WIDTH) : 0
    },
    puzzleStyle () {
      const height = this.stageWidth ? Math.round(this.stageWidth * this.puzzleHeight / BG_WIDTH) : 0
      const top = this.stageWidth ? Math.round(this.stageWidth * this.puzzleTop / BG_WIDTH) : 0
      return {
        width: this.puzzleDisplayWidth + 'px',
        height: height + 'px',
        left: this.pieceLeft + 'px',
        top: top + 'px'
      }
    },
    // 提示文字始终落在把手之外较空的一侧，避免被把手压住
    tipStyle () {
      if (!this.stageWidth) {
        return ''
      }
      const btnRight = this.offset + this.handleSize
      if (this.stageWidth - btnRight >= btnRight) {
        return 'left:' + btnRight + 'px;right:0'
      }
      return 'left:0;right:' + (this.stageWidth - btnRight) + 'px'
    },
    tipText () {
      const map = {
        loading: 'user.slider.loading',
        moving: 'user.slider.moving',
        verify: 'user.slider.verifying',
        success: 'user.slider.success',
        error: 'user.slider.error',
        loadFailed: 'user.slider.loadFailed'
      }
      return this.$t(map[this.status] || 'user.slider.drag')
    },
    handleIcon () {
      if (this.status === 'success') {
        return 'check'
      }
      if (this.status === 'error') {
        return 'close'
      }
      return 'double-right'
    },
    // 有 Touch Events 的内核把触摸交给 touchstart/touchmove/touchend：
    // 个别内核（iOS Safari、部分 Android WebView）的 pointer capture 在弹层里
    // 会丢失 up 或把 up 改成 cancel，表现就是拖到位松手没反应
    hasTouchEvents () {
      return typeof window !== 'undefined' && typeof window.TouchEvent !== 'undefined'
    }
  },
  mounted () {
    this.measure()
    window.addEventListener('resize', this.measure)
    /* 把手被别的层盖住时，事件根本进不到 handle 上，用户只觉“按住拖不动”。
       捕获阶段监听整块组件，按在把手外也留一条记录（不拦截、不改变默认行为） */
    this.$el.addEventListener('pointerdown', this.onDownProbe, true)
    this.$el.addEventListener('mousedown', this.onDownProbe, true)
    this.$el.addEventListener('touchstart', this.onDownProbe, true)
    window.addEventListener('error', this.onDiagError)
    mark('mount', this.diagGeo())
    /* 窗口大小不变、容器宽度变（弹窗动画收起、侧边栏收展、字体缩放）时
       resize 事件不会触发，拿不到宽度就永远不知道题面该多高。
       ResizeObserver 不支持的旧内核退回到只有 window.resize + 补量 */
    if (typeof ResizeObserver !== 'undefined' && this.$refs.stage) {
      this.resizeObserver = new ResizeObserver(this.measure)
      this.resizeObserver.observe(this.$refs.stage)
    }
  },
  beforeDestroy () {
    this.clearTimers()
    this.clearDiagTimers()
    if (this.measureFrame) {
      cancelAnimationFrame(this.measureFrame)
      this.measureFrame = null
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    window.removeEventListener('resize', this.measure)
    window.removeEventListener('error', this.onDiagError)
    if (this.$el) {
      this.$el.removeEventListener('pointerdown', this.onDownProbe, true)
      this.$el.removeEventListener('mousedown', this.onDownProbe, true)
      this.$el.removeEventListener('touchstart', this.onDownProbe, true)
    }
    this.detachDrag()
    // 弹窗关闭前把没发出去的失败现场带走，keepalive 保证页面卸载也能送达
    this.flushDiag('destroy', true)
  },
  methods: {
    clearDiagTimers () {
      if (this.noMoveTimer) {
        clearTimeout(this.noMoveTimer)
        this.noMoveTimer = null
      }
      if (this.releaseTimer) {
        clearTimeout(this.releaseTimer)
        this.releaseTimer = null
      }
    },
    // 以下三个方法只读现场、不改任何验证逻辑，全部走 try/catch 不影响主流程
    diagGeo () {
      return {
        stageWidth: this.stageWidth,
        stageHeight: this.stageHeight,
        handleSize: this.handleSize,
        // 把手可移动范围：小于等于 0 时拖多少都不动，是“拖不动”的硬特征
        moveMax: this.stageWidth - this.handleSize,
        puzzleDisplayWidth: this.puzzleDisplayWidth,
        pieceMax: this.stageWidth - this.puzzleDisplayWidth,
        scale: this.stageWidth ? Math.round(this.stageWidth / BG_WIDTH * 1000) / 1000 : 0
      }
    },
    diagDrag () {
      const d = this.diag || {}
      const pct = this.stageWidth ? this.pieceLeft / this.stageWidth * 100 : null
      return {
        mode: this.dragMode || d.mode || '',
        startClientX: this.dragStartX,
        releaseClientX: d.releaseX === undefined ? null : d.releaseX,
        lastMoveX: d.lastX === undefined ? null : d.lastX,
        offset: this.offset,
        pieceLeft: this.pieceLeft,
        pct: pct,
        moves: d.moves || 0,
        sinceDown: d.downAt ? Date.now() - d.downAt : 0,
        sinceLastMove: d.moveAt ? Date.now() - d.moveAt : 0
      }
    },
    flushDiag (reason, urgent) {
      try {
        flush(reason, {
          captchaId: this.captchaId,
          geo: this.diagGeo(),
          drag: this.diagDrag(),
          result: { status: this.status, errorCount: this.errorCount, measureTries: this.measureTries }
        }, urgent)
      } catch (e) { /* 诊断异常不能影响验证 */ }
    },
    inClass (target, name) {
      let node = target
      while (node && node !== this.$el) {
        const cls = node.className && node.className.toString ? node.className.toString() : ''
        if (cls.indexOf(name) >= 0) {
          return true
        }
        node = node.parentNode
      }
      return false
    },
    // 捕获阶段的“按在把手外”探针：把手画不出来或被遮挡时，这里是唯一能收到事件的地方
    onDownProbe (e) {
      try {
        const target = e.target
        const handle = this.$refs.handle
        if (!target || !target.tagName || target === document.body) {
          return
        }
        if (handle && (target === handle || handle.contains(target))) {
          return
        }
        // 换题按钮和加载/失败遮罩上的点击是正常操作
        if (this.inClass(target, 'sc-refresh') || this.inClass(target, 'sc-veil')) {
          return
        }
        const now = Date.now()
        if (now - this.lastProbeAt < 600) {
          return
        }
        if (!handle && this.status !== 'default' && this.status !== 'error' && this.status !== 'moving') {
          // 把手未渲染时（loading/success）不算异常
          return
        }
        this.lastProbeAt = now
        const x = this.pointerX(e)
        const y = e.touches && e.touches[0] ? e.touches[0].clientY : (e.clientY || 0)
        const topEl = document.elementFromPoint ? document.elementFromPoint(x, y) : null
        markFail('down_not_on_handle', {
          type: e.type,
          tag: target.tagName,
          cls: (target.className && target.className.toString ? target.className.toString() : '').slice(0, 60),
          // 该坐标上真正在最上层的元素：不一致就说明有东西盖住了把手
          topTag: topEl ? topEl.tagName : '',
          topCls: (topEl && topEl.className && topEl.className.toString ? topEl.className.toString() : '').slice(0, 60),
          handleRect: handle ? Math.round(handle.getBoundingClientRect().width) + 'x' + Math.round(handle.getBoundingClientRect().height) : '',
          point: Math.round(x) + ',' + Math.round(y)
        })
        this.flushDiag('down_not_on_handle')
      } catch (err) { /* ignore */ }
    },
    onDiagError (e) {
      try {
        markFail('js_error', {
          msg: e && e.message ? String(e.message).slice(0, 200) : '',
          file: e && e.filename ? String(e.filename).slice(-120) : '',
          line: e && e.lineno ? e.lineno : null,
          status: this.status,
          offset: this.offset
        })
        this.flushDiag('js_error', true)
      } catch (err) { /* ignore */ }
    },
    measure () {
      const stage = this.$refs.stage
      if (!stage) {
        return
      }
      const width = Math.round(stage.clientWidth)
      if (!width) {
        /* 弹窗打开动画期间或容器暂不可见时 clientWidth 为 0：
           不补量就会让 stageHeight 永远为 0，题面整块空白。
           限制重试次数，避免容器确实不可见时空跑 rAF */
        this.measureTries++
        /* 补量耗尽仍量不到宽度：题面高度为 0、把手行程为 0，
           对用户就是“题面出不来”或“按住拖不动”，需留下实测数据 */
        if (this.measureTries === 30) {
          markFail('measure_zero', { tries: this.measureTries, status: this.status, display: stage.currentStyle ? stage.currentStyle.display : '' })
          this.flushDiag('measure_zero')
        }
        if (!this.measureFrame && this.measureTries < 30 && typeof requestAnimationFrame !== 'undefined') {
          this.measureFrame = requestAnimationFrame(() => {
            this.measureFrame = null
            this.measure()
          })
        }
        return
      }
      this.measureTries = 0
      if (width !== this.stageWidth) {
        this.stageWidth = width
        mark('measure', { width: width, moveMax: width - this.handleSize, scale: Math.round(width / BG_WIDTH * 1000) / 1000 })
        // 容器变窄后旧偏移可能越界，先夹回轨道内
        const moveMax = Math.max(0, width - this.handleSize)
        if (this.offset > moveMax) {
          this.offset = moveMax
        }
        // 请求在途时不重复发：弹窗打开/字体加载都可能引起二次尺寸回调
        if (!this.bgSrc && !this.requesting) {
          this.load()
        }
      }
    },
    clearTimers () {
      if (this.watchdogTimer) {
        clearTimeout(this.watchdogTimer)
        this.watchdogTimer = null
      }
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
    },
    // 拉取新题面（背景图 + 拼图块）
    load () {
      this.clearTimers()
      this.status = 'loading'
      this.offset = 0
      this.captchaId = ''
      this.bgSrc = ''
      this.puzzleSrc = ''
      const seq = ++this.reqSeq
      this.requesting = true
      mark('gen_send', { stageWidth: this.stageWidth })
      this.watchdogTimer = setTimeout(() => {
        this.watchdogTimer = null
        if (seq === this.reqSeq && this.status === 'loading') {
          this.requesting = false
          this.status = 'loadFailed'
          // 题面请求发出去没任何回音：没有题面也就无从验证
          markFail('gen_timeout', { watchdog: WATCHDOG })
          this.flushDiag('gen_timeout')
        }
      }, WATCHDOG)
      axios.post('/user/login/slider/captcha/gen', {}).then(res => {
        if (seq !== this.reqSeq) {
          return
        }
        this.requesting = false
        this.clearTimers()
        const resp = res.data
        if (resp && resp.code === 0 && resp.data && resp.data.bg && resp.data.puzzle) {
          const data = resp.data
          this.captchaId = data.captchaId
          this.bgSrc = data.bg
          this.puzzleSrc = data.puzzle
          this.puzzleWidth = data.puzzleWidth || 60
          this.puzzleHeight = data.puzzleHeight || 60
          this.puzzleTop = data.puzzleTop || 0
          this.status = 'default'
          mark('gen_ok', { puzzleTop: this.puzzleTop, puzzleWidth: this.puzzleWidth })
        } else {
          this.status = 'loadFailed'
          markFail('gen_rejected', { code: resp ? resp.code : null, hasData: !!(resp && resp.data) })
          this.flushDiag('gen_rejected')
        }
      }).catch(err => {
        if (seq !== this.reqSeq) {
          return
        }
        this.requesting = false
        this.clearTimers()
        this.status = 'loadFailed'
        markFail('gen_error', { msg: err && err.message ? String(err.message).slice(0, 120) : '', status: err && err.response ? err.response.status : null })
        this.flushDiag('gen_error')
      })
    },
    onDragStart (e) {
      if (this.status === 'success' || this.status === 'verify' ||
          this.status === 'loading' || this.status === 'loadFailed') {
        return
      }
      // 触摸指针交给紧跟的 touchstart：触摸拖动统一走 Touch Events
      if (e.type === 'pointerdown' && e.pointerType === 'touch' && this.hasTouchEvents) {
        mark('down_skip_touch_pointer', { x: this.pointerX(e) })
        return
      }
      /* 同一次按下会先发 pointerdown、再补发 mousedown / touchstart：
         pointer 模式下紧跟的兼容事件直接忽略；其余情况（新的一次按下，
         或上一轮 up 事件丢失留下 dragging=true）先复位再起手，
         否则把手会卡在上一次手势里、之后怎么按都没反应 */
      if (this.dragging) {
        const isCompat = (this.dragMode === 'pointer' && e.type !== 'pointerdown') ||
          (this.dragMode === 'touch' && e.type === 'pointerdown' && e.pointerType === 'touch')
        if (isCompat) {
          mark('down_skip_compat', { type: e.type, mode: this.dragMode })
          return
        }
        const tracked = this.dragMode === 'touch' && e.type === 'touchstart' && this.activeTouchId !== null
        if (tracked && this.touchIn(e.touches, this.activeTouchId)) {
          // 上一根手指还压在屏上：这只是第二根手指乱按，跟着原手势继续即可
          mark('down_skip_second_finger', { x: Math.round(this.pointerX(e)) })
          return
        }
        /* 走到这里说明上一次手势的 up 没送到组件（触摸时上一根手指已抬起却没收到
           touchend，或 pointer/mouse 流整个丢了 up），而把手正停在用户拖好的位置上。
           这次重新按下就当他在催结果 —— 直接拿停住的位置去校验，
           不要再让他白拖第二次，这是对“拖到缺口松手没反应”最直接的兜底 */
        const pending = this.offset >= 1
        const lostReason = pending ? (tracked ? 'up_lost_retouch' : 'up_lost_repress') : 'up_lost_restart'
        markFail(lostReason, {
          offset: Math.round(this.offset * 10) / 10,
          moves: (this.diag || {}).moves || 0,
          prevMode: this.dragMode,
          prevType: e.type,
          pending: pending
        })
        this.dragging = false
        this.detachDrag()
        this.flushDiag(lostReason)
        if (pending) {
          this.verify()
          return
        }
      }
      // 失败后还会有一次延迟换题，用户抢先再拖时不能再被它清掉题面
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
      if (this.status === 'error') {
        this.offset = 0
      }
      // touchstart 取 changedTouches：touches 里可能混着更早落下的手指
      if (e.type === 'touchstart') {
        this.activeTouchId = this.touchId(e, e.changedTouches && e.changedTouches.length ? e.changedTouches : e.touches)
      }
      this.dragStartX = this.pointerX(e)
      this.dragging = true
      this.status = 'moving'
      this.dragMode = e.type === 'pointerdown' ? 'pointer' : (e.type === 'touchstart' ? 'touch' : 'mouse')
      if (this.dragMode === 'pointer') {
        this.activePointerId = e.pointerId
      }
      this.diag = {
        mode: this.dragMode,
        downAt: Date.now(),
        moves: 0,
        firstX: null,
        lastX: null,
        moveAt: 0,
        releaseX: null,
        reported: false
      }
      mark('down', {
        type: e.type,
        mode: this.dragMode,
        pointerType: e.pointerType || null,
        // 收得到 down 但后续什么都收不到时，这几个字段能区分事件模型差异
        hasTouches: !!(e.touches && e.touches.length),
        changed: !!(e.changedTouches && e.changedTouches.length),
        // 锁定的手指 id：多指场景下判断坐标取错人的唯一线索
        touchId: this.activeTouchId,
        buttons: typeof e.buttons === 'number' ? e.buttons : null,
        cancelable: e.cancelable === undefined ? null : !!e.cancelable,
        x: this.dragStartX
      })
      /* 按下后一直没有 move：手势被当成页面滚动/缩放，或命中的不是把手；
         拖动中一段空档无任何事件：up 被内核吞了。两者都是用户报的两个症状 */
      this.clearDiagTimers()
      this.noMoveTimer = setTimeout(this.checkNoMove, NO_MOVE_WAIT)
      this.releaseTimer = setTimeout(this.checkNoRelease, NO_RELEASE_WAIT)
      /* 三套监听全部挂上：不同内核对同一次手势派发的事件组合并不一致，
         多收到一个重复事件只是把 offset 设成同一个值，比漏掉 up 安全得多。
         handle 上也有 pointerup/mouseup/touchend 作为冒泡被拦时的兜底 */
      window.addEventListener('pointermove', this.onDragMove)
      window.addEventListener('mousemove', this.onDragMove)
      window.addEventListener('touchmove', this.onDragMove, { passive: false })
      window.addEventListener('pointerup', this.onDragEnd)
      window.addEventListener('mouseup', this.onDragEnd)
      window.addEventListener('touchend', this.onDragEnd)
      window.addEventListener('pointercancel', this.onDragCancel)
      window.addEventListener('touchcancel', this.onDragCancel)
      // 切窗口 / Alt+Tab 时收不到任何 up，同样需要复位
      window.addEventListener('blur', this.onDragCancel)
      /* 鼠标/触控笔才捕获指针：移出把手甚至移出窗口后仍能收到 up。
         触摸不走 pointer 流，不需要也不依赖它 */
      if (this.dragMode === 'pointer' && e.currentTarget && e.currentTarget.setPointerCapture) {
        try {
          e.currentTarget.setPointerCapture(e.pointerId)
        } catch (err) {}
      }
    },
    checkNoMove () {
      this.noMoveTimer = null
      const d = this.diag
      if (!this.dragging || !d || d.moves > 0) {
        return
      }
      /* 按住超过 NO_MOVE_WAIT 一个 move 都没收到：touch-action 没生效、
         passive 监听导致 preventDefault 失效、或该内核压根不给这个把手发 move */
      markFail('no_move_event', {
        mode: d.mode,
        sinceDown: Date.now() - d.downAt,
        moveMax: this.stageWidth - this.handleSize,
        startClientX: this.dragStartX
      })
      this.flushDiag('no_move_event')
    },
    checkNoRelease () {
      this.releaseTimer = null
      const d = this.diag
      if (!this.dragging || !d) {
        return
      }
      const idle = Date.now() - (d.moveAt || d.downAt)
      if (idle >= NO_RELEASE_WAIT) {
        // 同一轮只报一次，避免用户反复推动时刷屏
        if (!d.reported && d.moves > 0) {
          d.reported = true
          /* 拖过之后长时间无事件：松手那一下的 pointerup/mouseup/touchend
             没送到组件，把手停在缺口上什么都不发生 */
          markFail('no_release_event', { idle: idle, moves: d.moves, sinceDown: Date.now() - d.downAt, offset: this.offset })
          this.flushDiag('no_release_event')
        }
        return
      }
      // 拖动期间这个检查随事件链续期，不需要每个 move 重建定时器
      if (Date.now() - d.downAt < 30000) {
        this.releaseTimer = setTimeout(this.checkNoRelease, NO_RELEASE_WAIT - idle)
      }
    },
    detachDrag () {
      this.clearDiagTimers()
      /* 这里不做模式判断，四套监听器全摘一遍：
         移除不存在的监听器无副作用，却能避免模式判断有遗漏时
         把 window 上的拖动监听留在页面上 */
      window.removeEventListener('pointermove', this.onDragMove)
      window.removeEventListener('pointerup', this.onDragEnd)
      window.removeEventListener('pointercancel', this.onDragCancel)
      window.removeEventListener('mousemove', this.onDragMove)
      window.removeEventListener('mouseup', this.onDragEnd)
      /* 摘除时带上与注册时相同第三个参数：老内核把选项对象当成 useCapture，
         不传就会错判为捕获监听，导致这批 touchmove 永远摘不掉 */
      window.removeEventListener('touchmove', this.onDragMove, { passive: false })
      window.removeEventListener('touchend', this.onDragEnd)
      window.removeEventListener('touchcancel', this.onDragCancel)
      window.removeEventListener('blur', this.onDragCancel)
      this.dragMode = ''
      this.activePointerId = null
      this.activeTouchId = null
    },
    touchId (e, list) {
      const src = list || e.touches
      const t = src && src.length ? src[0] : null
      return t && t.identifier !== undefined ? t.identifier : null
    },
    // 从手指列表里找出被锁定的那根：找不到返回 null（旧内核无 identifier 时也返回 null）
    touchIn (list, id) {
      if (!list || id === null || id === undefined) {
        return null
      }
      for (let i = 0; i < list.length; i++) {
        if (list[i].identifier === id) {
          return list[i]
        }
      }
      return null
    },
    /* 触摸一律按 identifier 取坐标：多指时 touches[0] 可能是早就压在屏幕上的
       另一根手指，拿它算位移会让把手要么纹丝不动、要么跳到另一处 */
    pointerX (e) {
      if (e.touches && e.touches.length) {
        const self = this.touchIn(e.touches, this.activeTouchId) || this.touchIn(e.changedTouches, this.activeTouchId)
        if (self) {
          return self.clientX
        }
        // 拿不到 identifier 的旧内核：只能退回本帧第一根手指
        return (e.changedTouches && e.changedTouches.length ? e.changedTouches[0] : e.touches[0]).clientX
      }
      return e.clientX
    },
    // 松手点的横坐标：touchend 时 touches 已清空，要改看 changedTouches
    releaseX (e) {
      if (!e) {
        return null
      }
      const changed = e.changedTouches && e.changedTouches.length ? e.changedTouches : null
      const remaining = e.touches && e.touches.length ? e.touches : null
      const list = changed || remaining
      const point = (list ? this.touchIn(list, this.activeTouchId) : null) ||
        (list && list.length ? list[0] : null) || e
      const x = point.clientX
      return typeof x === 'number' && isFinite(x) ? x : null
    },
    onDragMove (e) {
      if (!this.dragging) {
        return
      }
      // 多指指针时只跟踪按下滑块的那一个
      if (this.dragMode === 'pointer' && this.activePointerId !== null &&
          e.pointerId !== undefined && e.pointerId !== this.activePointerId) {
        return
      }
      /* 触摸 move 里找不到锁定的那根手指，说明它已经抬起而 touchend 没送上来：
         按当前位移直接收尾，既不用等超时，也不用用户重拖一遍 */
      if (this.dragMode === 'touch' && this.activeTouchId !== null && e.touches &&
          !this.touchIn(e.touches, this.activeTouchId)) {
        const d = this.diag || {}
        markFail('up_lost_touch_gone', { offset: Math.round(this.offset * 10) / 10, moves: d.moves || 0 })
        // 与 up_lost_by_move 一致：先把“touchend 被吞”报出去再收尾，即使随后校验过了也留证
        this.flushDiag('up_lost_touch_gone')
        this.endDrag(null, 'up_lost_touch_gone')
        return
      }
      const d = this.diag
      /* 鼠标/触控笔按住期间 move 一定带 buttons>0；收到 buttons=0 的 move
         说 up 已被内核吞掉（“拖到位松手没反应”的典型特征），
         按当前位移直接收尾，不拿这个点重算坐标（手已经抬起来了） */
      if (this.dragMode !== 'touch' && d && d.moves > 0 && typeof e.buttons === 'number' && e.buttons === 0) {
        markFail('up_lost_by_move', { x: Math.round(this.pointerX(e)), offset: this.offset, sinceDown: Date.now() - d.downAt })
        // 先报丢失再收尾：即使随后校验过了，“up 被吞”本身就是兼容性证据
        this.flushDiag('up_lost_by_move')
        this.endDrag(null, 'up_lost_by_move')
        return
      }
      if (e.cancelable) {
        e.preventDefault()
      }
      const moveMax = this.stageWidth - this.handleSize
      let delta = this.pointerX(e) - this.dragStartX
      delta = Math.max(0, Math.min(delta, moveMax))
      this.offset = delta
      if (d) {
        // move 不逐条记录（一次拖动上百个），只采样首末与计数
        d.moves++
        d.lastX = this.pointerX(e)
        d.moveAt = Date.now()
        if (!d.firstX) {
          d.firstX = d.lastX
          mark('first_move', { type: e.type, x: d.lastX, delta: Math.round(delta * 10) / 10 })
        }
      }
    },
    onDragEnd (e) {
      this.endDrag(e, 'release')
    },
    endDrag (e, reason) {
      if (!this.dragging) {
        return
      }
      // 抬起的不是按下滑块的那根手指/那个指针，继续等它松开
      if (this.dragMode === 'pointer' && e && e.pointerId !== undefined &&
          e.pointerId !== this.activePointerId) {
        mark('up_skip_other_pointer', { type: e.type, pointerId: e.pointerId })
        return
      }
      /* 用松手点补最后一次位移：部分内核会合并/丢弃最后一个 move，
         不补的话把手会停在缺口前一点点，看着对上了却校验不通过 */
      const releaseX = this.releaseX(e)
      if (this.diag) {
        this.diag.releaseX = releaseX
      }
      if (releaseX !== null) {
        const moveMax = this.stageWidth - this.handleSize
        this.offset = Math.max(0, Math.min(releaseX - this.dragStartX, moveMax))
      }
      const d = this.diag || {}
      const moves = d.moves || 0
      mark('up', {
        type: e ? e.type : null,
        reason: reason,
        mode: this.dragMode,
        changed: !!(e && e.changedTouches && e.changedTouches.length),
        x: releaseX,
        offset: Math.round(this.offset * 10) / 10,
        moves: moves,
        sinceDown: d.downAt ? Date.now() - d.downAt : 0
      })
      this.dragging = false
      this.detachDrag()
      if (this.offset < 1) {
        this.status = 'default'
        /* 明明拖了很多下、位移却回到 0，或把手行程算成 0：
           这类“拖不动”不会发校验请求，只能靠这里留证据；
           真的只是轻轻点一下（没动过）则不算异常 */
        if (moves >= 3 || (this.stageWidth - this.handleSize) <= 0) {
          markFail('up_without_offset', { moves: moves, upType: e ? e.type : null, reason: reason })
          this.flushDiag('up_without_offset')
        }
        return
      }
      this.verify()
    },
    // 手势被系统/浏览器接管或窗口失焦：不计为验证失败，把把手退回起点
    onDragCancel (e) {
      if (!this.dragging) {
        return
      }
      /* 触摸拖动时浏览器常会同时给 pointer 流发一个 cancel，但它只代表
         pointer 流被接管，touch 序列还在继续，不能据此把拖动取消 */
      if (e && e.type === 'pointercancel' && this.dragMode === 'touch') {
        return
      }
      if (e && e.type === 'touchcancel' && this.dragMode === 'pointer') {
        return
      }
      const d = this.diag || {}
      mark('cancel', { type: e ? e.type : null, mode: this.dragMode, moves: d.moves || 0, offset: Math.round(this.offset * 10) / 10 })
      this.dragging = false
      this.detachDrag()
      this.offset = 0
      this.status = this.bgSrc ? 'default' : 'loadFailed'
      // 一个 move 都没收到就被 cancel：长按菜单/滚动/缩放把手势抢走了，用户只觉得“拖不动”
      if (!d.moves) {
        markFail('gesture_cancelled', { type: e ? e.type : null, mode: d.mode, sinceDown: d.downAt ? Date.now() - d.downAt : 0 })
        this.flushDiag('gesture_cancelled')
      }
    },
    nudge (step) {
      if (this.status === 'success' || this.status === 'verify') {
        return
      }
      if (this.retryTimer) {
        clearTimeout(this.retryTimer)
        this.retryTimer = null
      }
      const moveMax = this.stageWidth - this.handleSize
      this.offset = Math.max(0, Math.min(this.offset + step, moveMax))
      this.status = 'moving'
    },
    // 提交拼图左偏移占背景宽度的百分比：与渲染宽度无关，避免缩放误差
    verify () {
      if (!this.stageWidth) {
        /* 之前一直没量到宽度时，提交前再同步读一次容器宽度：能读到就照常发校验，
           不能让一次量取失败就变成“拖到位松手没反应”的死路。
           这里不能改调 measure()：它会顺带 load() 换题，把用户刚拖好的位移清零 */
        const stage = this.$refs.stage
        const width = stage ? Math.round(stage.clientWidth) : 0
        if (width) {
          this.stageWidth = width
        }
      }
      if (!this.stageWidth) {
        // 依旧量不到：下面全部计算都是除 0，只能上报现场
        markFail('verify_no_width', { offset: Math.round(this.offset * 10) / 10 })
        this.flushDiag('verify_no_width')
        return
      }
      if (this.status === 'success' || this.status === 'verify') {
        return
      }
      if (this.offset < 1) {
        this.status = 'default'
        return
      }
      const moveMax = this.stageWidth - this.handleSize
      const pct = moveMax > 0 ? this.pieceLeft / this.stageWidth * 100 : NaN
      // 用全局 isFinite（ES3 就有），不依赖 Number.isFinite，旧内核缺失时会直接抛错
      if (!isFinite(pct)) {
        this.offset = 0
        this.status = 'default'
        markFail('verify_pct_nan', { pieceLeft: this.pieceLeft, stageWidth: this.stageWidth, offset: this.offset })
        this.flushDiag('verify_pct_nan')
        return
      }
      if (!this.captchaId) {
        this.checkFailed('verify_no_captcha', {})
        return
      }
      this.status = 'verify'
      const seq = this.reqSeq
      const captchaId = this.captchaId
      const sentAt = Date.now()
      mark('verify_send', { pct: Math.round(pct * 100) / 100, pieceLeft: Math.round(this.pieceLeft * 10) / 10 })
      this.clearTimers()
      this.watchdogTimer = setTimeout(() => {
        this.watchdogTimer = null
        if (seq === this.reqSeq && this.status === 'verify') {
          this.checkFailed('check_timeout', { watchdog: WATCHDOG })
        }
      }, WATCHDOG)
      axios.post('/user/login/slider/captcha/check', {
        captchaId: captchaId,
        x: pct.toFixed(2)
      }).then(res => {
        if (seq !== this.reqSeq || captchaId !== this.captchaId) {
          return
        }
        this.clearTimers()
        const resp = res.data
        if (resp && resp.code === 0) {
          // 验证通过：整条轨迹直接丢弃，不占用日志
          clearTrace()
          this.status = 'success'
          this.errorCount = 0
          this.$emit('success', captchaId)
        } else {
          this.checkFailed('check_rejected', {
            code: resp ? resp.code : null,
            wait: Date.now() - sentAt,
            submitPct: Math.round(pct * 100) / 100
          })
        }
      }).catch(err => {
        if (seq !== this.reqSeq || captchaId !== this.captchaId) {
          return
        }
        this.clearTimers()
        this.checkFailed('check_error', {
          msg: err && err.message ? String(err.message).slice(0, 120) : '',
          httpStatus: err && err.response ? err.response.status : null,
          wait: Date.now() - sentAt,
          submitPct: Math.round(pct * 100) / 100
        })
      })
    },
    checkFailed (reason, detail) {
      this.clearTimers()
      this.errorCount++
      this.status = 'error'
      this.$emit('fail', this.errorCount)
      markFail(reason || 'check_failed', detail || {})
      // 缺口真实位置由服务端按 captchaId 补齐，这里只带提交值与时间线
      this.flushDiag(reason || 'check_failed')
      this.retryTimer = setTimeout(() => {
        this.retryTimer = null
        // 兜底超时后又收到成功响应时保留结果，不再换题
        if (this.status !== 'success') {
          this.load()
        }
      }, RETRY_DELAY)
    }
  }
}
</script>

<style lang="less" scoped>
@track: 44px;
@handle: 44px;

.slider-captcha {
  width: 100%;
}

/* 题面区：与滑轨同底色，加载完成前读起来是同一块面板 */
.sc-stage {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #0d1721;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  /* 拖动时不要把图片/文字选成高亮，也不弹长按菜单 */
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-touch-callout: none;
}

.sc-bg {
  display: block;
  width: 100%;
  height: 100%;
  /* 图片不参与指针命中：避免从图片上长按拖拽/保存，也与拖动逻辑无关 */
  pointer-events: none;
  -webkit-user-drag: none;
}

.sc-puzzle {
  position: absolute;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
  pointer-events: none;
  -webkit-user-drag: none;
}

.sc-veil {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(9, 16, 23, 0.78);
  color: #8fa7bd;

  &__text {
    margin-top: 8px;
    font-size: 12px;
  }
}

.is-loadFailed .sc-veil {
  cursor: pointer;

  &:hover {
    color: #cfe3f1;
  }
}

.sc-refresh {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  padding: 0;
  font-size: 13px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.62);
  background: rgba(9, 16, 23, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 3px;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;

  &:hover,
  &:focus-visible {
    color: #fff;
    border-color: rgba(56, 182, 240, 0.5);
    outline: none;
  }
}

/* 滑轨：比弹窗更暗的一条凹槽 */
.sc-track {
  position: relative;
  height: @track;
  background: #0b141d;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0 0 4px 4px;
  overflow: hidden;
  /* 触屏上告诉浏览器这块区域不接受滚动/缩放手势，
     否则手指一拖就被识别成页面滚动，滑块拖不动或拖到一半被抢走 */
  touch-action: none;
  -ms-touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
}

.sc-fill {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: rgba(56, 182, 240, 0.16);
}

.is-success .sc-fill {
  background: rgba(30, 158, 90, 0.22);
}

.is-error .sc-fill {
  background: rgba(206, 59, 59, 0.2);
}

.sc-tip {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 0 8px;
  font-size: 12.5px;
  line-height: 42px;
  letter-spacing: 0.02em;
  text-align: center;
  color: #8fa7bd;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.is-moving .sc-tip {
  opacity: 0;
}

.is-success .sc-tip {
  color: #4eae7f;
}

.is-error .sc-tip {
  color: #e0736a;
}

.sc-handle {
  position: absolute;
  top: 0;
  width: @handle;
  height: 42px;
  padding: 0;
  color: #fff;
  font-size: 14px;
  background: linear-gradient(135deg, #1290e0 0%, #0a76c6 100%);
  border: none;
  border-radius: 0 3px 3px 0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 2px 10px rgba(0, 0, 0, 0.45);
  cursor: grab;
  transition: background 0.2s;
  touch-action: none;
  -ms-touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(56, 182, 240, 0.55), inset 0 1px 0 rgba(255, 255, 255, 0.22);
  }
}

.is-success .sc-handle {
  background: #1e9e5a;
}

.is-error .sc-handle {
  background: #ce3b3b;
}

@media (prefers-reduced-motion: reduce) {
  .sc-handle,
  .sc-refresh {
    transition: none;
  }
}
</style>
